import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const SalesTax = () => {
  // State variables for Sales Tax calculation
  const [itemPrice, setItemPrice] = useState(0); // Price of the item
  const [salesTaxRate, setSalesTaxRate] = useState(0); // Sales tax rate in percentage
  const [totalPrice, setTotalPrice] = useState(0); // Calculated total price including tax

  // Event handlers to update state values
  const handleItemPriceChange = (e) => setItemPrice(parseFloat(e.target.value) || 0);
  const handleSalesTaxRateChange = (e) => setSalesTaxRate(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the total price when any input changes
  useEffect(() => {
    if (itemPrice && salesTaxRate) {
      const total = itemPrice + (itemPrice * salesTaxRate / 100); // Calculate total price after tax
      setTotalPrice(total);
    } else {
      setTotalPrice(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });

  }, [itemPrice, salesTaxRate]);

  return (
    <Layout title="Sales Tax Calculator" path="sales-tax">
      <section className="section-padding" id="sales-tax-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Sales Tax Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Sales Tax Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Item Price Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="itemPrice" className="form-label">
                        Item Price (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Item Price:</strong> The original price of the item before tax."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="itemPrice"
                        placeholder="Enter item price"
                        value={itemPrice || ""}
                        onChange={handleItemPriceChange}
                      />
                    </div>

                    {/* Sales Tax Rate Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="salesTaxRate" className="form-label">
                        Sales Tax Rate (%)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Sales Tax Rate:</strong> The percentage of tax to be added to the item price."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="salesTaxRate"
                        placeholder="Enter sales tax rate"
                        value={salesTaxRate || ""}
                        onChange={handleSalesTaxRateChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Price:{" "}
                        <span className="fw-bold ms-3">
                          {totalPrice === 0 ? "0.00" : totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Sales Tax Calculator
                </h3>

                {/* What is a Sales Tax Calculator? */}
                <h4 className="mt-3 mb-3">What is a Sales Tax Calculator?</h4>
                <p>
                  A **Sales Tax Calculator** helps consumers and businesses estimate the total price of an item after adding the applicable sales tax. It takes the item price and the sales tax rate to calculate the final price.
                </p>

                {/* Why Use a Sales Tax Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Sales Tax Calculator?</h4>
                <p>
                  This calculator is useful for businesses and consumers who need to calculate the total price of items after tax. It helps businesses comply with tax regulations and ensures transparency in pricing.
                </p>

                {/* Benefits of Using a Sales Tax Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Sales Tax Calculator</h4>
                <ul>
                  <li><strong>Ensures Tax Compliance for Businesses:</strong> Helps businesses calculate sales tax correctly and stay compliant with local regulations.</li>
                  <li><strong>Simplifies Tax Calculations for Consumers and Retailers:</strong> Makes it easier for both customers and retailers to quickly determine the final price of goods or services.</li>
                  <li><strong>Aids in Transparent Pricing for Customers:</strong> Provides clarity for customers regarding the taxes added to the cost of the items they purchase.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Sales Tax Calculator?</h4>
                <p>
                  The Sales Tax Calculator is useful for:
                </p>
                <ul>
                  <li><strong>Retailers:</strong> Ensures accurate calculation of sales tax to avoid undercharging or overcharging customers.</li>
                  <li><strong>E-commerce Platforms:</strong> Allows online stores to display correct prices, including taxes.</li>
                  <li><strong>Consumers:</strong> Helps consumers understand the final cost of their purchases, including taxes.</li>
                  <li><strong>Hospitality Industry:</strong> Used by restaurants and hotels to add appropriate sales tax to bills.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">How does the sales tax rate impact the total price?</h4>
                <p>
                  The sales tax rate directly affects the final price you pay for an item. A higher sales tax rate will increase the total price, while a lower sales tax rate will reduce the total cost.
                </p>

                <h4 className="mt-4 mb-3">Is sales tax the same everywhere?</h4>
                <p>
                  No, sales tax rates vary depending on the region or country. Different states or countries have different tax rates, and some goods or services may be exempt from sales tax.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SalesTax;
